import { z } from 'zod';

const id = z.string().min(1).max(160);
const text = z.string().trim().min(1).max(8000);
// References are stage-local. No arbitrary URL, file path or predecessor lookup.
export const RichtungsQuelleRefSchema = z.object({
  art: z.enum(['nachricht', 'entscheidung', 'vorschlag', 'material', 'menschenquelle']), id,
}).strict();
export const RichtungsQuelleSchema = RichtungsQuelleRefSchema.extend({
  text, am: z.string(), mensch: z.boolean(), provenance: text,
}).strict();
const AussageSchema = z.object({
  id, text, art: z.enum(['belegt', 'annahme', 'offen']),
  quellen: z.array(RichtungsQuelleRefSchema).max(40),
}).strict();
const FrageSchema = z.object({
  id, frage: text, luecke: text, aussagen: z.array(id).min(1).max(40),
  auswahl: z.enum(['einzeln', 'mehrfach', 'prioritaet', 'freitext']),
  kategorie: z.enum(['richtung', 'information', 'gestaltung']).optional(),
  zeitpunkt: text.optional(),
  aufgaben: z.array(z.object({ stepId: id, bezug: text }).strict()).max(40).optional(),
  gegenargument: text.optional(),
  optionen: z.array(z.object({ id, text, folge: text }).strict()).max(20),
  empfehlung: text.optional(),
  // Optional pointer to an existing native decision. Never implicitly answers it.
  nativeReferenz: RichtungsQuelleRefSchema.optional(),
}).strict().refine(q => q.auswahl === 'freitext' ? q.optionen.length === 0 : q.optionen.length >= 2, 'Sachangaben haben keine erfundenen Alternativen; Auswahlfragen brauchen mindestens zwei Optionen.');
export const RichtungsEntwurfSchema = z.object({
  zielvorschlag: z.object({ text, begruendung: text, fertigWenn: text }).strict().optional(),
  zusammenfassung: text, aussagen: z.array(AussageSchema).max(100),
  fragen: z.array(FrageSchema).max(50),
  arbeitsstrecke: text, unabhaengigeArbeit: z.string().max(8000).optional(),
}).strict();
export type RichtungsEntwurf = z.infer<typeof RichtungsEntwurfSchema>;
export const RichtungsPublikationSchema = z.object({
  basisVersion: z.number().int().nonnegative(), entwurf: RichtungsEntwurfSchema,
}).strict();
export const RichtungsAntwortSchema = z.object({
  eingabeId: z.string().uuid(), basisVersion: z.number().int().positive(),
  zielkorrektur: text.optional(),
  antworten: z.array(z.object({ frageId: id, optionen: z.array(id).max(20),
    einordnung: z.enum(['auswahl', 'anders', 'nicht_relevant']), notiz: z.string().max(8000).optional(),
  }).strict()).max(50),
  korrekturen: z.array(z.object({ aussageId: id,
    einordnung: z.enum(['trifft_zu', 'anders', 'nicht_relevant']),
    text: z.string().max(8000).optional(),
  }).strict()).max(100),
  ergaenzung: z.string().max(8000).optional(),
}).strict().refine(a => a.antworten.length + a.korrekturen.length > 0 || !!a.ergaenzung?.trim() || !!a.zielkorrektur?.trim(), 'Keine Eingabe gewählt.');
export type RichtungsAntwort = z.infer<typeof RichtungsAntwortSchema>;
export const RichtungsFassungSchema = z.object({
  version: z.number().int().positive(), am: z.string(), von: id,
  entwurf: RichtungsEntwurfSchema, quellen: z.array(RichtungsQuelleSchema),
}).strict();
export const RichtungsEingangSchema = z.object({
  version: z.number().int().positive(), am: z.string(), von: id,
  eingabe: RichtungsAntwortSchema,
  // Frozen human-readable context: survives revised questions, restart and compaction.
  kontext: text.max(200000),
}).strict();
export const RichtungDokumentSchema = z.object({
  schemaVersion: z.literal(1), version: z.number().int().nonnegative(),
  fassungen: z.array(RichtungsFassungSchema), eingaben: z.array(RichtungsEingangSchema),
}).strict();
export type RichtungDokument = z.infer<typeof RichtungDokumentSchema>;
export type RichtungsQuelle = z.infer<typeof RichtungsQuelleSchema>;
export type RichtungsQuelleRef = z.infer<typeof RichtungsQuelleRefSchema>;
export type RichtungsEingang = z.infer<typeof RichtungsEingangSchema>;
export function aktuelleRichtung(d?: RichtungDokument) { return d?.fassungen.at(-1); }

/** Reuse only answers to exactly the same question/alternatives/effects. A consultant
 * cannot change an option's meaning while retaining its previously selected id. */
export function gespeicherteRichtungsAntworten(d?: RichtungDokument) {
  const current = aktuelleRichtung(d);
  const antworten: Record<string, RichtungsAntwort['antworten'][number]> = {};
  const korrekturen: Record<string, RichtungsAntwort['korrekturen'][number]> = {};
  if (!d || !current) return { antworten, korrekturen };
  for (const e of d.eingaben) {
    const original = d.fassungen.filter(f => f.version <= e.eingabe.basisVersion).at(-1);
    if (!original) continue;
    for (const a of e.eingabe.antworten) {
      const before = original.entwurf.fragen.find(f => f.id === a.frageId);
      const now = current.entwurf.fragen.find(f => f.id === a.frageId);
      if (before && now && JSON.stringify(before) === JSON.stringify(now)) antworten[a.frageId] = a;
    }
    for (const a of e.eingabe.korrekturen) {
      const before = original.entwurf.aussagen.find(s => s.id === a.aussageId);
      const now = current.entwurf.aussagen.find(s => s.id === a.aussageId);
      if (before && now && before.text === now.text) korrekturen[a.aussageId] = a;
    }
  }
  return { antworten, korrekturen };
}
